import { Constants } from '../const/constants';
import { TabSdk } from '@techextensor/tab-sdk';

export class FtpImageLoaderUtils {

  private static readonly DEFAULT_PLACEHOLDER_SIZE = '150px';
  private static readonly FTP_URL_PATTERN = Constants.FILEMANAGER_URL + Constants.GETIMAGE;

  /**
   * Scans for FTP images and wraps each with a spinner placeholder.
   * Accepts a Document (modifies in-place) or an HTML string (parses internally).
   */
  static addPlaceholders(input: Document | string) {
    if (typeof input === 'string') {
      if (!input) return { html: input, ftpImages: [] };
      const doc = new DOMParser().parseFromString(input, 'text/html');
      const ftpImages = this.scanAndWrapImages(doc);
      return { html: doc.body.innerHTML, ftpImages };
    }
    return this.scanAndWrapImages(input);
  }

  /**
   * Fetches a single FTP image using TabSdk.file.processFtpUrl.
   * Returns blob URL by default (efficient, for live DOM contexts).
   * Pass asDataUrl=true for serialized HTML contexts where blob URLs don't survive.
   */
  static async fetchImage(src: string, useDefaultHeaders: boolean, asDataUrl = false): Promise<string> {
    const blobUrl = await TabSdk.file.processFtpUrl({ data: src, defaultHeader: useDefaultHeaders });
    if (!blobUrl) throw new Error('Failed to fetch FTP image');

    if (!asDataUrl) return blobUrl;

    // Convert blob URL to data URL for innerHTML contexts
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    URL.revokeObjectURL(blobUrl);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Replaces a placeholder wrapper with the loaded image.
   * Unwraps the wrapper, sets the image URL on the img, and restores visibility.
   */
  static replacePlaceholder(doc: Document, placeholderId: string, imageUrl: string): void {
    const wrapper = doc.querySelector(
      `span.tab-ftp-image-placeholder[data-ftp-placeholder-id="${placeholderId}"]`
    );
    if (!wrapper) return;

    const img = wrapper.querySelector(
      `img[data-ftp-placeholder-id="${placeholderId}"]`
    ) as HTMLImageElement;

    if (img) {
      img.src = imageUrl;
      img.removeAttribute('data-ftp-placeholder-id');
      img.removeAttribute('data-ftp-src');
      img.style.removeProperty('display');
      wrapper.parentNode?.replaceChild(img, wrapper);
    } else {
      wrapper.parentNode?.removeChild(wrapper);
    }
  }

  /**
   * Safety net: removes any leftover placeholder wrappers from a Document.
   * Used on the save path to ensure no placeholder markup is persisted.
   */
  static cleanupPlaceholders(doc: Document): void {
    doc.querySelectorAll('span.tab-ftp-image-placeholder').forEach(wrapper => {
      const img = wrapper.querySelector('img');
      if (img) {
        img.removeAttribute('data-ftp-placeholder-id');
        img.removeAttribute('data-ftp-src');
        img.style.removeProperty('display');
        wrapper.parentNode?.replaceChild(img, wrapper);
      } else {
        wrapper.parentNode?.removeChild(wrapper);
      }
    });

    doc.querySelectorAll('img[data-ftp-placeholder-id]').forEach(img => {
      img.removeAttribute('data-ftp-placeholder-id');
      img.removeAttribute('data-ftp-src');
      (img as HTMLElement).style.removeProperty('display');
    });
  }

  /**
   * Core logic: finds FTP images in a Document, wraps with spinner placeholders.
   */
  private static scanAndWrapImages(doc: Document) {
    const ftpImages: any = [];
    const imgTags = doc.querySelectorAll('img');

    for (let i = 0; i < imgTags?.length; i++) {
      const img = imgTags[i];
      const src = img.src;

      if (!src || !src.includes(this.FTP_URL_PATTERN)) continue;

      const placeholderId = TabSdk.util.generateUniqueId('ftp-img-');
      const useDefaultHeaders = img.classList.contains('processWithDefaultHeaders');
      ftpImages.push({ id: placeholderId, src, useDefaultHeaders });

      // Determine placeholder dimensions from existing img attributes/styles
      const width = this.toCssSize(img.getAttribute('width') || img.style.width) || this.DEFAULT_PLACEHOLDER_SIZE;
      const height = this.toCssSize(img.getAttribute('height') || img.style.height) || this.DEFAULT_PLACEHOLDER_SIZE;

      // Store original src and hide the image
      img.setAttribute('data-ftp-placeholder-id', placeholderId);
      img.setAttribute('data-ftp-src', src);
      img.removeAttribute('src');
      img.style.display = 'none';

      // Create wrapper with spinner
      const wrapper = doc.createElement('span');
      wrapper.className = 'tab-ftp-image-placeholder';
      wrapper.setAttribute('data-ftp-placeholder-id', placeholderId);
      wrapper.style.width = width;
      wrapper.style.height = height;

      const spinner = doc.createElement('span');
      spinner.className = 'tab-ftp-spinner';

      // Replace img with wrapper containing spinner + hidden img
      img.parentNode?.replaceChild(wrapper, img);
      wrapper.appendChild(spinner);
      wrapper.appendChild(img);
    }

    return ftpImages;
  }

  /**
   * Normalizes a size value to a valid CSS size string.
   * Bare numbers (e.g. "300") get "px" appended; values with units are returned as-is.
   */
  private static toCssSize(value: string | null | undefined): string {
    if (!value) return '';
    const trimmed = value.trim();
    if (!trimmed || trimmed === 'auto') return '';
    return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
  }
}